import React, { useState } from 'react';
import { Shield, X, QrCode, AlertCircle } from 'lucide-react';
import Button from '../../shared/Button';
import { useEnableTwoFactor, useDisableTwoFactor } from '../../../services/vendorProfileService';

const TwoFactorAuthTab = () => {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);

  const enableTwoFactorMutation = useEnableTwoFactor({
    onSuccess: (data) => {
      setQrCode(data.qr_code);
      setBackupCodes(data.backup_codes);
      setTwoFactorEnabled(true);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to enable two-factor authentication';
      console.error('2FA enable failed:', errorMessage);
    }
  });

  const disableTwoFactorMutation = useDisableTwoFactor({
    onSuccess: () => {
      setTwoFactorEnabled(false);
      setQrCode('');
      setBackupCodes([]);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to disable two-factor authentication';
      console.error('2FA disable failed:', errorMessage);
    }
  });

  const handleEnableTwoFactor = () => {
    enableTwoFactorMutation.mutate();
  };

  const handleDisableTwoFactor = () => {
    disableTwoFactorMutation.mutate();
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Two-Factor Authentication</h3>
        <p className="text-sm text-gray-600">
          Add an extra layer of security to your account with two-factor authentication.
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-3 flex-shrink-0 ${twoFactorEnabled ? 'bg-green-500' : 'bg-gray-400'}`} />
            <div className="min-w-0">
              <h4 className="font-medium text-gray-900 text-sm sm:text-base">
                {twoFactorEnabled ? 'Two-Factor Authentication Enabled' : 'Two-Factor Authentication Disabled'}
              </h4>
              <p className="text-xs sm:text-sm text-gray-600">
                {twoFactorEnabled 
                  ? 'Your account is protected with two-factor authentication'
                  : 'Enable two-factor authentication for enhanced security'
                }
              </p>
            </div>
          </div>
          
          <Button
            onClick={twoFactorEnabled ? handleDisableTwoFactor : handleEnableTwoFactor}
            variant={twoFactorEnabled ? 'outline' : 'primary'}
            size="sm"
            className="flex items-center justify-center w-full sm:w-auto"
          >
            {twoFactorEnabled ? (
              <>
                <X className="w-4 h-4 mr-2" />
                Disable
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" />
                Enable
              </>
            )}
          </Button>
        </div>

        {twoFactorEnabled && qrCode && (
          <div className="mt-4 sm:mt-6 p-4 bg-white rounded-lg border">
            <h5 className="font-medium text-gray-900 mb-3 text-sm sm:text-base">Setup Complete</h5>
            <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex-shrink-0 mx-auto sm:mx-0">
                <div className="w-32 h-32 bg-white border-2 border-gray-200 rounded-lg flex items-center justify-center">
                  <QrCode className="w-8 h-8 text-gray-400" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-600 mb-3">
                  Scan the QR code with your authenticator app to complete setup.
                </p>
                <div className="space-y-2">
                  <h6 className="font-medium text-gray-900 text-sm">Backup Codes:</h6>
                  <div className="grid grid-cols-2 gap-2">
                    {backupCodes.map((code, index) => (
                      <div key={index} className="font-mono text-xs sm:text-sm bg-gray-100 px-2 py-1 rounded break-all">
                        {code}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    Save these backup codes in a safe place. You can use them to access your account if you lose your device.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="text-sm font-medium text-blue-900">Security Tip</h4>
            <p className="text-sm text-blue-700 mt-1">
              Two-factor authentication adds an extra layer of security by requiring a second form of verification 
              in addition to your password. We recommend using an authenticator app like Google Authenticator or Authy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuthTab;

